import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import history from 'common/history';
import { fetchFileDetails } from 'features/case/redux/fetchFileDetails';
import { setCurrentSelectedFile } from 'features/case/redux/setCurrentSelectedFile';
import { setFileToCompare } from 'features/case/redux/setFileToCompare';
import { setDocSearch } from 'features/viewing/redux/setDocSearch';
import { setDocSearchForCompare } from 'features/viewing/redux/setDocSearchForCompare';
import { setSidebarOpen } from 'features/common/redux/navbarLayout';
import { resetAskAiSse } from 'features/case/redux/askAiSse';
import { clearAISearchState } from 'features/case/redux/setAISearchState';
import {
  DEFAULT_NAVIGATION_HIGHLIGHT_COLOR,
  COMPARE_NAVIGATION_HIGHLIGHT_COLOR,
} from 'features/viewing/AcusoftViewer/AcusoftViewerControl/constants';

const normalizeCoordinates = (coordinates: any) => {
  if (!coordinates) return null;
  let coordsObj =
    typeof coordinates === 'string'
      ? JSON.parse(coordinates)
      : typeof coordinates === 'object'
        ? coordinates
        : null;
  if (!coordsObj) return null;

  if (Array.isArray(coordsObj)) {
    if (coordsObj.length === 0) return null;
    if (coordsObj.length === 1) return coordsObj[0];

    const first = coordsObj[0];
    const last = coordsObj[coordsObj.length - 1];
    if (!first || typeof first !== 'object') return null;
    if (!last || typeof last !== 'object') return first;

    const startValue = (first as any).startChar ?? (first as any).startIndex;
    const endValue = (last as any).endChar ?? (last as any).endIndex;
    const combined: Record<string, any> = { ...first };

    if (startValue != null) {
      combined.startIndex = startValue;
      combined.startChar = startValue;
    }
    if (endValue != null) {
      combined.endIndex = endValue;
      combined.endChar = endValue;
    }

    if (startValue != null && endValue != null) {
      const startNum = typeof startValue === 'string' ? Number(startValue) : startValue;
      const endNum = typeof endValue === 'string' ? Number(endValue) : endValue;
      if (Number.isFinite(startNum) && Number.isFinite(endNum)) {
        combined.textLength = endNum - startNum;
      }
    }

    return combined;
  }

  return coordsObj;
};

export function useCitationNavigation(params: {
  caseId: any;
  documents: any[];
  effectiveChatId: string | null;
  fileSidebarAskAI?: boolean;
}) {
  const { caseId, documents, effectiveChatId, fileSidebarAskAI } = params;
  const dispatch = useDispatch();

  // Cache for storing fetchFileDetails results to prevent repetitive API calls when clicking citations.
  const fileDetailsCache = useRef<Record<string, any>>({});

  const resetCitationCache = useCallback(() => {
    fileDetailsCache.current = {};
  }, []);

  const loadFileWithCache = useCallback(
    async (fileId: string, onSuccess: (fileDetails: any) => void) => {
      if (!caseId || !fileId) return null;

      const cacheKey = `${caseId}-${fileId}`;
      const cachedFileDetails = fileDetailsCache.current[cacheKey];
      if (cachedFileDetails) {
        onSuccess(cachedFileDetails);
        return cachedFileDetails;
      }

      const fileFromDocuments =
        documents && documents.length > 0
          ? documents.find((doc: any) => doc?.id === fileId || doc?.fileId === fileId)
          : null;

      if (fileFromDocuments) {
        fileDetailsCache.current[cacheKey] = fileFromDocuments;
        onSuccess(fileFromDocuments);
        return fileFromDocuments;
      }

      try {
        const res: any = await dispatch(
          fetchFileDetails({ caseId, fileId, firstTrialBookOnly: true }) as any,
        );
        const updateFileObjectWithLocation =
          res?.bundleLocations && res.bundleLocations.length > 0
            ? { ...res.bundleLocations[0], ...res }
            : res;
        fileDetailsCache.current[cacheKey] = updateFileObjectWithLocation;
        onSuccess(updateFileObjectWithLocation);
        return updateFileObjectWithLocation;
      } catch (error) {
        console.error('Error fetching file details:', error);
        return null;
      }
    },
    [caseId, dispatch, documents],
  );

  const parseCoordinatesToLocation = useCallback((coordinates: any) => {
    if (!coordinates) return null;
    try {
      const coordsObj = normalizeCoordinates(coordinates);
      if (!coordsObj) return null;
      if (typeof coordsObj !== 'object') return null;

      const toNum = (v: any) => {
        const n = typeof v === 'string' ? Number(v) : v;
        return Number.isFinite(n) ? Number(n) : null;
      };

      const pageNumber = toNum(coordsObj.pageNumber);
      const startIndex = toNum(coordsObj.startIndex ?? coordsObj.startChar);
      const endIndex = toNum(coordsObj.endIndex ?? coordsObj.endChar);
      const x = toNum(coordsObj.x);
      const y = toNum(coordsObj.y);
      const textLength =
        toNum(coordsObj.textLength) ??
        (startIndex != null && endIndex != null ? endIndex - startIndex : null);

      // Viewer highlight code assumes these exist; if any are missing, fall back to text-only search.
      if (
        pageNumber == null ||
        startIndex == null ||
        x == null ||
        y == null ||
        textLength == null
      ) {
        return null;
      }

      return {
        pageNumber,
        startIndex,
        x,
        y,
        textLength,
        // Force mark button availability in AI-tools contexts; viewer also has URL fallbacks.
        showMarkButton: true,
        timestamp: Date.now(),
      };
    } catch {
      return null;
    }
  }, []);

  const buildNavigationItemFromCitation = useCallback(
    (citationObj: any) => {
      const metadata = citationObj?.metadata || {};
      const fileId = metadata?.fileId || metadata?.id || citationObj?.fileId;
      if (!fileId) return null;

      const isDocumentCitation = citationObj?.type === 'document';

      const text =
        metadata?.text ||
        citationObj?.text ||
        citationObj?.indexedText ||
        citationObj?.originalText ||
        citationObj?.enrichedText ||
        '';

      const location = isDocumentCitation
        ? null
        : parseCoordinatesToLocation(citationObj?.coordinates ?? metadata?.coordinates);

      return {
        fileId: String(fileId),
        citationType: citationObj?.type || metadata?.type,
        text: isDocumentCitation ? '' : String(text || ''),
        location,
      };
    },
    [parseCoordinatesToLocation],
  );

  const normalizeCompareFiles = useCallback((compareFiles?: string[]) => {
    if (!Array.isArray(compareFiles)) return [];
    return compareFiles.map((file) => String(file ?? '').trim()).filter(Boolean);
  }, []);

  const getCurrentFileId = useCallback(() => {
    const parts = (history.location.pathname || '').split('/').filter(Boolean);
    const filesIndex = parts.indexOf('files');
    if (filesIndex === -1) return '';
    return parts[filesIndex + 1] || '';
  }, []);

  const handleItemClick = useCallback(
    (item: any, compareFiles?: string[]) => {
      const fileId = item?.fileId;
      if (!fileId) return;

      const isDocumentCitation = item?.citationType === 'document';

      // Document citations open the file fresh at page 1 with no highlight. Wipe any
      // leftover docSearch state from a prior citation click so the new viewer instance
      // doesn't re-apply a stale coordinate-based highlight.
      if (isDocumentCitation) {
        dispatch(setDocSearch('', null) as any);
        dispatch(setDocSearchForCompare('', null) as any);
      }

      const normalizedCompareFiles = normalizeCompareFiles(compareFiles);
      const hasCompareFiles = normalizedCompareFiles.length > 1;
      const getHighlightColorForFile = (id: string): string | undefined => {
        if (!normalizedCompareFiles.length) return undefined;
        if (normalizedCompareFiles[0] && id === normalizedCompareFiles[0]) {
          return DEFAULT_NAVIGATION_HIGHLIGHT_COLOR;
        }
        if (normalizedCompareFiles.length > 1 && normalizedCompareFiles.slice(1).includes(id)) {
          return COMPARE_NAVIGATION_HIGHLIGHT_COLOR;
        }
        return undefined;
      };

      const currentFileId = getCurrentFileId();
      const location = item?.location ? { ...item.location, timestamp: Date.now() } : null;
      const highlightColor = getHighlightColorForFile(String(fileId));
      const isGreenCompareFile =
        normalizedCompareFiles.length > 1 &&
        normalizedCompareFiles.slice(1).includes(String(fileId));
      const searchText = item?.text || '';
      const currentSearchParams = new URLSearchParams(history.location.search || '');
      const isFocusView =
        currentSearchParams.get('focus') === 'true' ||
        currentSearchParams.get('focus') === '1' ||
        currentSearchParams.has('focused');

      if (hasCompareFiles && currentFileId && String(fileId) !== String(currentFileId)) {
        loadFileWithCache(String(fileId), (fileDetails) => {
          if (!fileSidebarAskAI) {
            dispatch(setSidebarOpen(false));
          }
          dispatch(setFileToCompare(fileDetails) as any);
          const compareLocation = location
            ? {
                ...location,
                isCompare: true,
                ...(highlightColor ? { highlightColor } : {}),
              }
            : null;
          if (!isDocumentCitation) {
            setTimeout(() => {
              dispatch(setDocSearchForCompare(searchText, compareLocation) as any);
            }, 1000);
          }
        });
        return;
      }

      let handledFocusViewNavigation = false;
      if (fileSidebarAskAI && isFocusView) {
        const currentFocused = currentSearchParams.get('focused');
        if (!currentFocused || String(currentFocused) !== String(fileId)) {
          dispatch(resetAskAiSse());
          dispatch(clearAISearchState());
        }
        currentSearchParams.set('focused', String(fileId));
        currentSearchParams.delete('focus');
        const nextSearch = currentSearchParams.toString();
        history.push({
          pathname: history.location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: history.location.hash || '',
          state: history.location.state,
        });
        handledFocusViewNavigation = true;
      }

      if (!handledFocusViewNavigation) {
        let newPath = history.location.pathname;
        const trailingParams = isDocumentCitation ? '?askAI=true&goToPage=1' : '?askAI=true';

        // If we are in ai-tools, we want to anchor to .../ai-tools/{chatId}
        if (newPath.includes('/ai-tools')) {
          const aiToolsMatch = newPath.match(/(.*\/ai-tools)/);
          if (aiToolsMatch) {
            const basePath = aiToolsMatch[1]; // .../ai-tools
            if (effectiveChatId) {
              newPath = `${basePath}/${effectiveChatId}/files/${fileId}${trailingParams}`;
            } else {
              newPath = `${basePath}/files/${fileId}${trailingParams}`;
            }
          }
        } else {
          // Fallback for non-ai-tools context (if any)
          const basePath = newPath.replace(/\/files.*/, ``);
          newPath = `${basePath}/files/${fileId}${trailingParams}`;
        }

        const currentState = history.location.state;
        const baseState =
          currentState && typeof currentState === 'object'
            ? (currentState as Record<string, any>)
            : {};
        history.push(newPath, { ...baseState, skipAskAiHydration: true });
      }

      const docSearchLocation = location
        ? {
            ...location,
            ...(isGreenCompareFile ? { isCompare: true } : {}),
            ...(highlightColor ? { highlightColor } : {}),
          }
        : null;

      let dispatched = false;
      const dispatchDocSearchOnce = () => {
        if (dispatched) return;
        if (isDocumentCitation) return;
        dispatched = true;
        dispatch(setDocSearch(searchText, docSearchLocation) as any);
      };

      // Always attempt doc search shortly after navigation; also attempt again after file load.
      if (!isDocumentCitation) {
        setTimeout(dispatchDocSearchOnce, 250);
      }
      loadFileWithCache(String(fileId), (fileDetails) => {
        dispatch(setCurrentSelectedFile(fileDetails) as any);
        if (!isDocumentCitation) {
          setTimeout(dispatchDocSearchOnce, 50);
        }
      });
    },
    [
      dispatch,
      effectiveChatId,
      fileSidebarAskAI,
      getCurrentFileId,
      loadFileWithCache,
      normalizeCompareFiles,
    ],
  );

  const handleCitationClick = useCallback(
    (source: string, citationsMap: any, compareFiles?: string[]) => {
      const citationObj = citationsMap?.[String(source)];
      if (!citationObj) {
        console.warn('AskAiSSE citation click: missing citation object for source', source);
        return;
      }

      const item = buildNavigationItemFromCitation(citationObj);
      if (!item) {
        console.warn(
          'AskAiSSE citation click: citation missing fileId/metadata',
          source,
          citationObj,
        );
        return;
      }
      handleItemClick(item, compareFiles);
    },
    [buildNavigationItemFromCitation, handleItemClick],
  );

  return { handleCitationClick, resetCitationCache };
}
